import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ChevronLeft, ChevronRight, FileBarChart, User, Plus, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const mondayOf = (d) => {
  const dt = new Date(d);
  const day = dt.getDay() || 7;
  if (day !== 1) dt.setDate(dt.getDate() - (day - 1));
  return dt.toISOString().slice(0, 10);
};

const money = (n) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
};

export default function Reports() {
  const { getAuthHeaders } = useAuth();
  const [view, setView] = useState('weekly'); // weekly | monthly
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/timeclock/employees`, { headers: getAuthHeaders() })
      .then(r => setEmployees(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-3" data-testid="tc-reports-section">
      <div className="flex gap-2">
        <Button variant={view === 'weekly' ? 'default' : 'outline'} onClick={() => setView('weekly')}
          className={view === 'weekly' ? 'bg-[#1D3C6E]' : ''} data-testid="tc-view-weekly">
          <FileBarChart className="w-4 h-4 mr-1" /> Reporte Semanal
        </Button>
        <Button variant={view === 'monthly' ? 'default' : 'outline'} onClick={() => setView('monthly')}
          className={view === 'monthly' ? 'bg-[#1D3C6E]' : ''} data-testid="tc-view-monthly">
          <User className="w-4 h-4 mr-1" /> Historial Mensual
        </Button>
      </div>
      {view === 'weekly' ? <WeeklyReport /> : <MonthlyReport employees={employees} />}
    </div>
  );
}

function WeeklyReport() {
  const { getAuthHeaders } = useAuth();
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState({ rows: [], week_start: '', week_end: '' });
  const [localRows, setLocalRows] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/timeclock/weekly-report?week_start=${weekStart}`, { headers: getAuthHeaders() });
      setData(r.data);
      setLocalRows(r.data.rows || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const recalcRow = (row) => {
    const sueldo = (row.regular_hours || 0) * (row.hourly_wage || 0);
    const totalSinDesc = sueldo + (row.importe_horas_extras || 0) + (row.prima_vacacional || 0);
    const totalDep = totalSinDesc - (row.prestamos || 0) - (row.retenciones_impuestos || 0) - (row.discounts_total || 0);
    // Saldo de vacaciones restantes = balance actual del empleado (BD).
    // Misma fuente que el módulo de Vacaciones.
    const saldoDespues = (row.vacation_days_balance || 0);
    return { ...row, sueldo, total_sin_descuentos: totalSinDesc, total_depositado: totalDep, saldo_despues: saldoDespues };
  };

  const updateField = (employeeId, field, value) => {
    setLocalRows(prev => prev.map(r => {
      if (r.employee_id !== employeeId) return r;
      const num = value === '' || value === '-' ? 0 : parseFloat(value);
      const next = { ...r, [field]: isNaN(num) ? 0 : num };
      return recalcRow(next);
    }));
  };

  const totals = localRows.reduce((acc, r) => ({
    sueldo: acc.sueldo + (r.sueldo || 0),
    importe_he: acc.importe_he + (r.importe_horas_extras || 0),
    prima: acc.prima + (r.prima_vacacional || 0),
    prestamos: acc.prestamos + (r.prestamos || 0),
    retenciones: acc.retenciones + (r.retenciones_impuestos || 0),
    sin_desc: acc.sin_desc + (r.total_sin_descuentos || 0),
    depositado: acc.depositado + (r.total_depositado || 0),
  }), { sueldo: 0, importe_he: 0, prima: 0, prestamos: 0, retenciones: 0, sin_desc: 0, depositado: 0 });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="tc-week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Input type="date" className="h-9 w-40 text-sm" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} />
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="tc-week-next"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm text-slate-600 ml-2">
            Semana: <span className="font-medium">{data.week_start}</span> → <span className="font-medium">{data.week_end}</span>
          </span>
        </div>
        <div className="flex items-center gap-6 text-right">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Total sin desc.</p>
            <p className="text-base font-semibold text-slate-700">{money(totals.sin_desc)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Préstamos + Reten.</p>
            <p className="text-base font-semibold text-rose-600">-{money(totals.prestamos + totals.retenciones)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Depositado</p>
            <p className="text-2xl font-bold text-[#1D3C6E]" data-testid="tc-week-total-depositado">{money(totals.depositado)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-2 py-2.5 whitespace-nowrap">Colaborador</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Horas Trabajadas</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap bg-amber-50">Horas Extras</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Sueldo</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap bg-amber-50">Préstamos</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap bg-amber-50">Importe Horas Extras</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap bg-amber-50">Retenciones e Impuestos</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap bg-amber-50">Prima Vacacional</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Total sin descuentos</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Total Depositado</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Días Vac. Usados</th>
              <th className="text-right px-2 py-2.5 whitespace-nowrap">Días Vac. Restantes</th>
              <th className="px-2 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {localRows.length === 0 ? (
              <tr><td colSpan={13} className="text-center py-8 text-slate-400 text-xs">Sin datos</td></tr>
            ) : localRows.map(r => (
              <PayrollRow
                key={r.employee_id}
                row={r}
                weekStart={data.week_start}
                onLocalChange={updateField}
                expanded={expanded === r.employee_id}
                onToggle={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)}
                reload={load}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 px-1">
        Las columnas en <span className="bg-amber-50 px-1">amarillo</span> son editables — los valores se guardan automáticamente al salir del campo.
      </p>
    </div>
  );
}

function EditableCell({ value, onLocalChange, onSave, testid, allowNegative = false }) {
  const [val, setVal] = useState(value ?? 0);
  const [focused, setFocused] = useState(false);
  // Sync external value into local when not focused
  useEffect(() => { if (!focused) setVal(value ?? 0); }, [value, focused]);

  const display = focused ? val : (Number(val) || 0).toFixed(2);

  return (
    <input
      type="text"
      inputMode="decimal"
      className="w-full text-right text-xs h-7 px-1.5 border border-amber-200 rounded bg-amber-50 hover:bg-amber-100 focus:bg-white focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-300"
      value={display}
      onFocus={(e) => { setFocused(true); setVal(value ?? 0); setTimeout(() => e.target.select(), 0); }}
      onChange={(e) => {
        const raw = e.target.value;
        // Allow empty, minus sign only if allowed, digits, dot
        const cleaned = raw.replace(allowNegative ? /[^0-9.\-]/g : /[^0-9.]/g, '');
        setVal(cleaned);
        onLocalChange(cleaned);
      }}
      onBlur={() => {
        setFocused(false);
        const num = val === '' || val === '-' ? 0 : parseFloat(val);
        onSave(isNaN(num) ? 0 : num);
      }}
      data-testid={testid}
    />
  );
}

function PayrollRow({ row, weekStart, onLocalChange, expanded, onToggle, reload }) {
  const { getAuthHeaders } = useAuth();
  const savingRef = useRef({});

  const saveField = async (field, value) => {
    if (!weekStart) return;
    // Skip duplicate saves
    if (savingRef.current[field] === value) return;
    savingRef.current[field] = value;
    try {
      await axios.post(`${API}/api/timeclock/weekly-adjustments`, {
        employee_id: row.employee_id,
        week_start: weekStart,
        [field]: value,
      }, { headers: getAuthHeaders() });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error saving adjustment', err);
    }
  };

  const saldoCls = (row.saldo_despues || 0) < 0 ? 'text-rose-600' : 'text-slate-700';
  const vacCls = (row.vacation_days_used || 0) < 0 ? 'text-rose-600' : 'text-purple-700';

  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/60">
        <td className="px-2 py-1.5 font-medium whitespace-nowrap" data-testid={`tc-row-name-${row.employee_id}`}>{row.employee_name}</td>
        <td className="px-2 py-1.5 text-right" data-testid={`tc-row-worked-${row.employee_id}`}>{(row.regular_hours || 0).toFixed(2)}</td>
        <td className="px-1 py-1 text-right">
          <EditableCell value={row.horas_extras} onLocalChange={(v) => onLocalChange(row.employee_id, 'horas_extras', v)}
            onSave={(v) => saveField('horas_extras', v)} testid={`tc-input-he-${row.employee_id}`} />
        </td>
        <td className="px-2 py-1.5 text-right text-slate-700" data-testid={`tc-row-sueldo-${row.employee_id}`}>{money(row.sueldo)}</td>
        <td className="px-1 py-1 text-right">
          <EditableCell value={row.prestamos} onLocalChange={(v) => onLocalChange(row.employee_id, 'prestamos', v)}
            onSave={(v) => saveField('prestamos', v)} testid={`tc-input-prestamos-${row.employee_id}`} />
        </td>
        <td className="px-1 py-1 text-right">
          <EditableCell value={row.importe_horas_extras} onLocalChange={(v) => onLocalChange(row.employee_id, 'importe_horas_extras', v)}
            onSave={(v) => saveField('importe_horas_extras', v)} testid={`tc-input-importe-he-${row.employee_id}`} />
        </td>
        <td className="px-1 py-1 text-right">
          <EditableCell value={row.retenciones_impuestos} onLocalChange={(v) => onLocalChange(row.employee_id, 'retenciones_impuestos', v)}
            onSave={(v) => saveField('retenciones_impuestos', v)} testid={`tc-input-retenciones-${row.employee_id}`} />
        </td>
        <td className="px-1 py-1 text-right">
          <EditableCell value={row.prima_vacacional} onLocalChange={(v) => onLocalChange(row.employee_id, 'prima_vacacional', v)}
            onSave={(v) => saveField('prima_vacacional', v)} testid={`tc-input-prima-${row.employee_id}`} />
        </td>
        <td className="px-2 py-1.5 text-right font-medium text-slate-700" data-testid={`tc-row-sindesc-${row.employee_id}`}>{money(row.total_sin_descuentos)}</td>
        <td className="px-2 py-1.5 text-right font-bold text-[#1D3C6E]" data-testid={`tc-row-depositado-${row.employee_id}`}>{money(row.total_depositado)}</td>
        <td className={`px-2 py-1.5 text-right ${vacCls}`}>{(row.vacation_days_used || 0).toFixed(2)}</td>
        <td className={`px-2 py-1.5 text-right font-medium ${saldoCls}`} data-testid={`tc-row-saldo-${row.employee_id}`}>{(row.saldo_despues || 0).toFixed(2)}</td>
        <td className="px-2 py-1.5 text-right whitespace-nowrap">
          <button onClick={onToggle} className="text-[11px] text-[#1D3C6E] hover:underline" data-testid={`tc-toggle-discounts-${row.employee_id}`}>
            Otros desc.{(row.discounts_total || 0) > 0 ? ` (${(row.discounts || []).length})` : ''}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={13} className="px-3 py-3">
            <DiscountsPanel employee={row} weekStart={weekStart} onChanged={reload} onClose={onToggle} />
          </td>
        </tr>
      )}
    </>
  );
}

function DiscountsPanel({ employee, weekStart, onChanged, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [concept, setConcept] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');

  const items = employee.discounts || [];

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/api/timeclock/discounts`, {
        employee_id: employee.employee_id,
        concept, amount: parseFloat(amount) || 0, week_start: weekStart, note,
      }, { headers: getAuthHeaders() });
      setConcept(''); setAmount(''); setNote('');
      onChanged();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    const reason = window.prompt('Motivo para eliminar el descuento:');
    if (!reason) return;
    try {
      await axios.delete(`${API}/api/timeclock/discounts/${id}`, {
        headers: getAuthHeaders(), data: { reason }
      });
      onChanged();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/timeclock/discounts?employee_id=${employee.employee_id}`, { headers: getAuthHeaders() });
      setHistory(r.data);
      setShowHistory(true);
    } catch { /* noop */ }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`tc-discounts-panel-${employee.employee_id}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[#1D3C6E]">Otros descuentos — {employee.employee_name} (semana {weekStart})</h4>
        <div className="flex items-center gap-2">
          <button onClick={loadHistory} className="text-xs text-slate-500 hover:underline" data-testid={`tc-show-history-${employee.employee_id}`}>Historial completo</button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
      </div>
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map(d => (
            <div key={d.id} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded text-sm">
              <div>
                <span className="font-medium">{d.concept}</span>
                {d.note && <span className="text-xs text-slate-500 ml-2">— {d.note}</span>}
                <span className="text-[11px] text-slate-400 ml-2">por {d.applied_by} · {new Date(d.created_at).toLocaleString('es-MX')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-rose-600">-${(d.amount || 0).toFixed(2)}</span>
                <button onClick={() => remove(d.id)} className="text-red-500 hover:text-red-700" data-testid={`tc-del-disc-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end pt-2 border-t border-slate-100">
        <div className="md:col-span-4">
          <label className="text-xs text-slate-500 block">Concepto *</label>
          <Input value={concept} onChange={(e) => setConcept(e.target.value)} required placeholder="Credito empresa" data-testid={`tc-disc-concept-${employee.employee_id}`} />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-slate-500 block">Monto *</label>
          <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid={`tc-disc-amount-${employee.employee_id}`} />
        </div>
        <div className="md:col-span-4">
          <label className="text-xs text-slate-500 block">Nota</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional" />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" size="sm" className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid={`tc-disc-add-${employee.employee_id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
          </Button>
        </div>
        {error && <p className="md:col-span-12 text-xs text-rose-500">{error}</p>}
      </form>

      {showHistory && (
        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Historial completo</h5>
            <button onClick={() => setShowHistory(false)} className="text-xs text-slate-400 hover:text-slate-600">Ocultar</button>
          </div>
          {history.length === 0 ? (
            <p className="text-xs text-slate-400">Sin descuentos registrados</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left py-1.5">Fecha</th>
                    <th className="text-left py-1.5">Semana</th>
                    <th className="text-left py-1.5">Concepto</th>
                    <th className="text-left py-1.5">Nota</th>
                    <th className="text-right py-1.5">Monto</th>
                    <th className="text-left py-1.5">Aplicado por</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-t border-slate-100">
                      <td className="py-1.5 text-slate-500">{new Date(h.created_at).toLocaleDateString('es-MX')}</td>
                      <td className="py-1.5">{h.week_start}</td>
                      <td className="py-1.5 font-medium">{h.concept}</td>
                      <td className="py-1.5 text-slate-500">{h.note || '—'}</td>
                      <td className="py-1.5 text-right font-medium text-rose-600">-${(h.amount || 0).toFixed(2)}</td>
                      <td className="py-1.5 text-slate-500">{h.applied_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MonthlyReport({ employees }) {
  const { getAuthHeaders } = useAuth();
  const [empId, setEmpId] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const load = async () => {
    if (!empId) return;
    try {
      const r = await axios.get(`${API}/api/timeclock/employee/${empId}/monthly?month=${month}`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [empId, month]);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 bg-white p-3 rounded-lg border border-slate-200">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Empleado</label>
          <select className="h-9 px-2 border border-slate-200 rounded-md text-sm min-w-[180px]"
            value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="tc-monthly-emp">
            <option value="">Seleccionar empleado</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Mes</label>
          <Input type="month" className="h-9 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
      </div>

      {!empId ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400 text-sm">Selecciona un empleado para ver su historial mensual</div>
      ) : !data ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400 text-sm">Cargando...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Horas trabajadas" value={data.totals.regular_hours.toFixed(2)} accent="primary" />
            <Stat label="Sueldo" value={money(data.totals.sueldo)} />
            <Stat label="Importe H. Extras" value={money(data.totals.importe_horas_extras)} accent="amber" />
            <Stat label="Prima Vacacional" value={money(data.totals.prima_vacacional)} />
            <Stat label="Préstamos" value={money(data.totals.prestamos)} accent="red" />
            <Stat label="Retenciones" value={money(data.totals.retenciones_impuestos)} accent="red" />
            <Stat label="Otros descuentos" value={money(data.totals.discounts_total)} accent="red" />
            <Stat label="Total Depositado" value={money(data.totals.total_depositado)} accent="primary" />
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2.5">Semana</th>
                  <th className="text-right px-2 py-2.5">H. Trab.</th>
                  <th className="text-right px-2 py-2.5">H. Extras</th>
                  <th className="text-right px-2 py-2.5">Sueldo</th>
                  <th className="text-right px-2 py-2.5">Imp. H.E.</th>
                  <th className="text-right px-2 py-2.5">Prima Vac.</th>
                  <th className="text-right px-2 py-2.5">Préstamos</th>
                  <th className="text-right px-2 py-2.5">Retenciones</th>
                  <th className="text-right px-2 py-2.5">Sin desc.</th>
                  <th className="text-right px-2 py-2.5">Depositado</th>
                  <th className="text-right px-2 py-2.5">Vac. Usados</th>
                  <th className="text-right px-2 py-2.5">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map(w => (
                  <tr key={w.week_start} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-2">{w.week_start} → {w.week_end}</td>
                    <td className="px-2 py-2 text-right">{w.regular_hours.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right">{(w.horas_extras || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right">{money(w.sueldo)}</td>
                    <td className="px-2 py-2 text-right">{money(w.importe_horas_extras)}</td>
                    <td className="px-2 py-2 text-right">{money(w.prima_vacacional)}</td>
                    <td className="px-2 py-2 text-right text-rose-600">{money(w.prestamos)}</td>
                    <td className="px-2 py-2 text-right text-rose-600">{money(w.retenciones_impuestos)}</td>
                    <td className="px-2 py-2 text-right">{money(w.total_sin_descuentos)}</td>
                    <td className="px-2 py-2 text-right font-bold text-[#1D3C6E]">{money(w.total_depositado)}</td>
                    <td className={`px-2 py-2 text-right ${(w.vacation_days_used || 0) < 0 ? 'text-rose-600' : 'text-purple-700'}`}>{(w.vacation_days_used || 0).toFixed(2)}</td>
                    <td className={`px-2 py-2 text-right font-medium ${(w.saldo_despues || 0) < 0 ? 'text-rose-600' : 'text-slate-700'}`}>{(w.saldo_despues || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colors = {
    amber: 'text-amber-700',
    red: 'text-red-600',
    purple: 'text-purple-700',
    primary: 'text-[#1D3C6E]',
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${colors[accent] || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
